import Feather from '@expo/vector-icons/Feather';
import { useNavigation } from '@react-navigation/native';
import { Text, Spin, Header, DateRangePicker } from '_/components';
import Button from '_/components/Button';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import themes from '_/constants/themes';
import { AppRoute, CredentialingProps } from '_/routes/types';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, FlatList, Platform, StyleSheet, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { UserCompanyRole } from '../../models/enums';
import EmptyMeetings from '../Meetings/ActiveMeetings/EmptyMeetings';
import MeetingCard from '../Meetings/MeetingCard';
import moment from 'moment';
import { meetingsApi } from '_/services/api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MeetingsModel } from '@w3lcome/types';
import { getMeetingDetailsSuccess, newMeetingRequest } from '_/store/modules/meeting/actions';
import { queryClient } from '_/services/queryClient';
import { showMessage } from 'react-native-flash-message';

const { width } = Dimensions.get('window');

const DEFAULT_LIMIT = 100;

export default function Credentialing() {
  const navigation = useNavigation<CredentialingProps>();

  const dispatch = useDispatch();

  const companyId = useSelector((state: any) => state.company.id);
  const userId = useSelector((state: any) => state.user.id);

  const [reachedEnd, setReachedEnd] = useState(false);

  const [startDate, setStartDate] = useState(moment().startOf('day')?.toDate());
  const [endDate, setEndDate] = useState(moment().endOf('day')?.toDate());

  const getCredentialing = async (skip = 0) => {
    try {
      const response = await meetingsApi.getMeetings({
        start: endDate ?? startDate,
        end: startDate,
        $limit: DEFAULT_LIMIT,
        $skip: skip,
        companyId,
        isCredentialing: true,
        inviteesTable: true,
        '$sort[start]': -1,
      });

      return response;
    } catch (error) {
      showMessage({
        message: translate('personalMeeting.errorTryAgainLater'),
        type: 'danger',
      });
    }
  };

  const { data } = useQuery({
    queryKey: ['getCredentialing', startDate?.toISOString(), endDate?.toISOString(), userId],
    enabled: !!startDate && !!endDate,
    queryFn: async () => await getCredentialing(),
    staleTime: 1000 * 60 * 5,
  });

  const { t: translate } = useTranslation();

  const { loading } = useSelector((state: any) => state.loading);
  const { role } = useSelector((state: any) => state.host);

  function handleGetMeetingsDetails(meeting: MeetingsModel) {
    dispatch(getMeetingDetailsSuccess(meeting));
    navigation.navigate(AppRoute.MEETING, {
      isCredentialing: true,
      onChange: addOrUpdateCredentialing.mutate,
    });
  }

  function handleChangeDates(startDate: Date, endDate: Date) {
    setEndDate(endDate);
    setStartDate(startDate);
  }

  const addOrUpdateCredentialing = useMutation({
    mutationFn: async (credentialing: MeetingsModel) => credentialing,
    onSuccess: useCallback(
      (credentialing: MeetingsModel) => {
        queryClient.setQueryData(
          ['getCredentialing', startDate?.toISOString(), endDate?.toISOString(), userId],
          (oldData: any | undefined) => {
            if (!oldData || oldData?.data?.length === 0) return [credentialing];

            const existsIndex = oldData?.data?.findIndex(
              (item: any) => item.id === credentialing.id
            );

            if (existsIndex !== -1) {
              const updated = [...oldData?.data];
              updated[existsIndex] = credentialing;

              return {
                ...oldData,
                data: updated,
              };
            }

            return {
              ...oldData,
              data: [credentialing, ...oldData?.data],
            };
          }
        );
      },
      [startDate, endDate]
    ),
  });

  async function handleNextPagination(skip: number) {
    if (data && data?.data?.length < DEFAULT_LIMIT) return;

    const response = await getCredentialing(skip);

    queryClient.setQueryData(
      ['getCredentialing', startDate?.toISOString(), endDate?.toISOString()],
      (oldData: any) => {
        if (!response) return oldData;

        const updatedList = [...(oldData?.data || []), ...response.data];

        const updatedTotal = Number(oldData.total + response.total);

        if (response.data.length < DEFAULT_LIMIT) {
          setReachedEnd(true);
        }

        return {
          ...oldData,
          data: updatedList,
          total: updatedTotal,
        };
      }
    );
  }

  function handleCreateCredentialing() {
    dispatch(newMeetingRequest());
    navigation.navigate(AppRoute.EDIT_MEETING, {
      isCredentialing: true,
      onChange: addOrUpdateCredentialing.mutate,
    });
  }

  const loadMore = useCallback(async () => {
    try {
      if (!!data?.data?.length && !reachedEnd) {
        await handleNextPagination(data?.data?.length);
      }
    } catch (error) {
      showMessage({
        message: translate('personalMeeting.errorTryAgainLater'),
        type: 'danger',
      });
    }
  }, [data, reachedEnd]);

  return (
    <ContainerWidthLimit>
      <View style={styles.container}>
        <Spin visible={loading} />
        <Header handleBack={() => navigation.goBack()} title={translate('credentialing')} />

        <View style={styles.datePickerContainer}>
          <DateRangePicker
            selectedEndDate={endDate}
            selectedStartDate={startDate}
            handleChangeDates={handleChangeDates}
          />
        </View>
        <FlatList
          onEndReached={loadMore}
          onEndReachedThreshold={0.2}
          ListEmptyComponent={<EmptyMeetings />}
          style={styles.content}
          contentContainerStyle={{ paddingBottom: 80 }}
          data={data?.data}
          keyExtractor={(item) => item.id}
          renderItem={({ item: meeting }) => (
            <MeetingCard meeting={meeting} getMeetingDetails={handleGetMeetingsDetails} />
          )}
        />

        {[
          UserCompanyRole.ADMIN,
          UserCompanyRole.MANAGER,
          UserCompanyRole.FRONTDESK,
          UserCompanyRole.HOST,
        ]?.includes(role) && (
          <Button onPress={handleCreateCredentialing} buttonStyles={styles.btnCreateMeeting}>
            <Feather name="plus" style={styles.icon} />
            <Text style={styles.text}>{translate('newCredentialing')}</Text>
          </Button>
        )}
      </View>
    </ContainerWidthLimit>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: themes.colors.white,
    flex: 1,
  },
  btnCreateMeeting: {
    alignSelf: 'center',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    bottom: 24,
    height: 44,
    borderRadius: 6,
    ...(Platform.OS === 'web' ? {} : { width: width - 48 }),
  },
  icon: {
    fontSize: 14,
    color: themes.colors.white,
  },
  text: {
    fontSize: 18,
    color: themes.colors.white,
    marginLeft: 8,
  },
  content: {
    flex: 1,
    paddingHorizontal: 8,
  },
  datePickerContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    height: 50,
    marginVertical: 16,
  },
});
